import type { ActionReducerMapBuilder, Draft, PayloadAction, SliceCaseReducers } from '@reduxjs/toolkit';
import type { Operation } from 'fast-json-patch';
import { applyPatch } from 'fast-json-patch';
import cloneDeep from 'lodash-es/cloneDeep';
import type { Extent } from 'ol/extent';

import { assert, assertNonNullable } from '@disy/lib/custom-error';
import { getLogger } from '@disy/lib/logging';
import { timeout } from '@disy/lib/timeout';
import { keys } from '@disy/lib/type-util';

import type { Data } from 'ui/data/data';

import type { ChangesRequestDto } from 'cadenza/api-client/dto/changes-request-dto';
import type { ObjecttypesDataDependenciesDto } from 'cadenza/api-client/dto/objecttype-data-dependencies-dto';
import type { Problem } from 'cadenza/api-client/dto/problem-dto';
import {
  type ExecuteCommandResult,
  type GetWorkbookStateResult,
  executeCommand as executeCommandRequest,
  getObjecttypesDataDependencies,
  getPartialWorkbookState,
  getWorkbookState,
} from 'cadenza/api-client/workbook-api';
import { Command, isCommandType } from 'cadenza/api-client/workbook-commands';
import {
  isActiveWorksheetOnDemand,
  isWorksheetOnDemand,
  isWorksheetOnDemandOverlayVisible,
  shouldOnDemandBeConsidered,
  updateIsOnDemandWorksheetLoading,
} from 'cadenza/map/on-demand/on-demand-map.state';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id.gen';
import type { WorkbookNameAndHash } from 'cadenza/repository/model/workbook-name-and-hash';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import { createAsyncThunk } from 'cadenza/store/cadenza-store';
import { STATUS } from 'cadenza/store/state-utils';
import type { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { ViewType } from 'cadenza/workbook/api/view-type';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import type {
  NotificationSource,
  WorkbookNotification,
  WorkbookNotificationId,
} from 'cadenza/workbook/notifications/workbook-notifications';
import {
  getMapViewContext,
  getOverviewMapContextByMainMapViewId,
  isMainMapView,
  isOverviewMap,
} from 'cadenza/workbook/state/workbook-map-view-selectors.state';
import {
  forcePanelsHidden,
  isPanelVisible,
  updatePanelStateFromDisplayState,
} from 'cadenza/workbook/state/workbook-panel.state';
import { validateMapViewLayers, validateViews } from 'cadenza/workbook/view-validation/view-validation';
import { createInitialWorkbookState } from 'cadenza/workbook/workbook-core-initial-state';
import {
  getActiveView,
  getActiveWorksheet,
  getActiveWorksheetViews,
  getDataViewIdsUsingObjecttype,
  getDataViewIdsUsingObjecttypes,
  getMaximizedViewId,
  getNotificationsForViewByNotificationId,
  getViewById,
  getViewByLayerId,
  getViews,
  getWorkbookNameAndHash,
  isActiveWorksheetDirty,
  isRepositoryPublishingProcessEnabled,
  layerById,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { SLICE_PREFIX, slice } from 'cadenza/workbook/workbook-core-slice.state';
import { WORKBOOK_ERROR_KEYS } from 'cadenza/workbook/workbook-error-keys';
import type { ViewMetadata, ViewWithMetadata, WorkbookState } from 'cadenza/workbook/workbook.state';

const logger = getLogger('cadenza/workbook');

export const coreReducers = {
  // Should be a thunk, but too complicated for now
  updateWorkbookMetadata(
    state,
    {
      payload: {
        workbookNameAndHash = state.workbookNameAndHash,
        printName = state.printName,
        version = state.version,
        publicationStage = state.publicationStage,
        saved = state.saved,
        changed = state.changed,
        clientChanged = state.clientChanged,
        activeWorksheetId = state.activeWorksheetId,
        reportMode = state.reportMode,
      },
    }: PayloadAction<Partial<WorkbookState>>,
  ) {
    if (saved && state.saved) {
      changed = 'no';
      clientChanged = false;
    }
    if (!isRepositoryPublishingProcessEnabled(state)) {
      publicationStage = undefined;
    }
    Object.assign(state, {
      workbookNameAndHash,
      workbookName: workbookNameAndHash?.id ?? state.workbookName,
      printName,
      saved,
      changed,
      clientChanged,
      version,
      publicationStage,
      activeWorksheetId,
      reportMode,
    });
  },
  updateWorkbookChanged(state, { payload: { changed = state.changed, clientChanged = state.clientChanged } }) {
    Object.assign(state, {
      changed,
      clientChanged,
    });
  },
  updateViewMetadata: {
    prepare: (viewId: ViewId, metadata: ViewMetadata) => ({ payload: { viewId, metadata } }),
    reducer: (
      state,
      {
        payload: { viewId, metadata },
      }: PayloadAction<{
        viewId: ViewId;
        metadata: ViewMetadata;
      }>,
    ) => {
      let viewMetadata = state.viewMetadataById[viewId];
      if (!viewMetadata) {
        viewMetadata = {};
        state.viewMetadataById[viewId] = viewMetadata;
      }
      Object.entries(metadata).forEach(([key, value]) => {
        if (value !== undefined) {
          viewMetadata[key] = value;
        } else {
          delete viewMetadata[key];
        }
      });
    },
  },
  removeObjecttypes(state, { payload }) {
    Object.assign(state, removeObjecttypesById(state, payload));
  },
  activateView(state, { payload: viewId }) {
    activateViewInternally(state, viewId);
  },
  maximizeView(state, { payload: viewId }) {
    state.maximizedViewId = viewId;
  },
  forceMaximizeView(state, { payload: viewId }: { payload: ViewId | undefined }) {
    if (viewId) {
      state.forceMaximizePreviousState = {
        maximizedViewId: state.maximizedViewId,
        // cloneDeep() cannot be replaced with structuredClone() in this case,
        // because that fails to clone the Immer proxies.
        panelById: cloneDeep(state.panelById),
      };

      state.maximizedViewId = viewId;
      forcePanelsHidden(state);
    } else {
      Object.assign(state, state.forceMaximizePreviousState);
      delete state.forceMaximizePreviousState;
    }
  },
  setDetailView(state, { payload: viewId }) {
    state.detailViewId = viewId;
  },
  initializeWorksheet(state) {
    activateOnlyViewIfAvailable(state);
  },
  unmarkViewAsDirty(state, { payload: viewId }) {
    const view = getViewById(state, viewId);
    const dataViewIds = view.dataViews;

    state.invalidViewsOrDataViews = state.invalidViewsOrDataViews.filter(
      (viewIdOrDataViewId) => view.id !== viewIdOrDataViewId && !dataViewIds.includes(viewIdOrDataViewId as DataViewId),
    );
    state.dataViewsAffectedBySelectionChange = state.dataViewsAffectedBySelectionChange.filter(
      (dataViewId) => !dataViewIds.includes(dataViewId),
    );
    delete state.invalidLayerIdsByViewId[viewId];
  },
  invalidateViewsOrDataViews(state, { payload: viewOrDataViewIds }: PayloadAction<(ViewId | DataViewId)[]>) {
    viewOrDataViewIds.forEach((viewOrDataViewId) => {
      if (!state.invalidViewsOrDataViews.includes(viewOrDataViewId)) {
        state.invalidViewsOrDataViews.push(viewOrDataViewId);
      }
    });
  },
  invalidateDataViewsUsingObjecttypes(state, { payload: objecttypeIds }) {
    const dataViewIdsUsingObjecttype = getDataViewIdsUsingObjecttypes(state, objecttypeIds);

    for (const dataViewId of dataViewIdsUsingObjecttype) {
      if (!state.invalidViewsOrDataViews.includes(dataViewId)) {
        state.invalidViewsOrDataViews.push(dataViewId);
      }
    }
  },
  invalidateDataViewsUsingObjecttype(state, { payload: objecttypeId }) {
    const dataViewIdsUsingObjecttype = getDataViewIdsUsingObjecttype(state, objecttypeId);

    for (const dataViewId of dataViewIdsUsingObjecttype) {
      if (!state.invalidViewsOrDataViews.includes(dataViewId)) {
        state.invalidViewsOrDataViews.push(dataViewId);
      }
    }
  },
  invalidateLayerIds(state, { payload: invalidLayerIds }: PayloadAction<LayerId[]>) {
    invalidLayerIds.forEach((layerId) => {
      const viewId = getViewByLayerId(state, layerId).id;
      const invalidLayerIdsByViewId = state.invalidLayerIdsByViewId[viewId];
      if (invalidLayerIdsByViewId) {
        if (!invalidLayerIdsByViewId.includes(layerId)) {
          invalidLayerIdsByViewId.push(layerId);
        }
      } else {
        state.invalidLayerIdsByViewId[viewId] = [layerId];
      }
    });
  },
  updateObjecttypeBoundState(state, { payload: { objecttypeId, boundState } }) {
    const objecttype = slice(state).objecttypeById[objecttypeId];
    objecttype.boundState = boundState;
  },
  updateObjecttypeBoundStateOnSave(state) {
    Object.values(slice(state).objecttypeById).forEach((objecttype) => {
      if (objecttype.boundState === 'TRANSIENT') {
        objecttype.boundState = 'BOUND_TO_WORKBOOK';
      }
    });
  },
  updateWorkbookPrintName(state, { payload }) {
    state.printName = payload;
  },
  removeNotifications(
    state,
    { payload: { viewId, removedIds } }: { payload: { viewId: ViewId; removedIds: WorkbookNotificationId[] } },
  ) {
    const viewNotifications = getNotificationsForViewByNotificationId(state, viewId);
    removedIds.forEach((removedId) => delete viewNotifications[removedId]);
  },
  removeNotificationsBySource(
    state,
    { payload: { viewId, source } }: { payload: { viewId: ViewId; source: NotificationSource } },
  ) {
    removeNotificationBySource(state, viewId, source);
  },
  replaceNotificationsBySource(
    state,
    {
      payload: { viewId, source, notifications },
    }: { payload: { viewId: ViewId; source: NotificationSource; notifications: WorkbookNotification[] } },
  ) {
    removeNotificationBySource(state, viewId, source);
    doAddNotifications(state, viewId, notifications);
  },
  addNotifications(
    state,
    { payload: { viewId, added } }: { payload: { viewId: ViewId; added: WorkbookNotification[] } },
  ) {
    doAddNotifications(state, viewId, added);
  },
  resetActiveLayerId(state) {
    delete state.activeLayerId;
  },
  setActiveLayerId(state, { payload }: { payload: LayerId }) {
    state.activeLayerId = payload;
  },
  removeUnusedObjecttypeIds(state, { payload: { objecttypeIds } }) {
    state.unavailableObjecttypeIds = state.unavailableObjecttypeIds.filter((u) => !objecttypeIds.includes(u));
  },
} satisfies SliceCaseReducers<WorkbookState>;

export const addCoreExtraReducers = (builder: ActionReducerMapBuilder<WorkbookState>) => {
  return builder
    .addCase(fetchInitialState.pending, (state) => {
      state.initializationStatus = STATUS.PENDING;
    })
    .addCase(fetchInitialState.fulfilled, (state, action) => {
      // We reinitialize the state to the initial state and set the current workbookNameAndHash on top
      // (we don't want the initial workbookNameAndHash), this is necessary when the Save as actions is triggered
      // from a workbook because we really want to reinitialize the store from scratch
      const workbookNameAndHash = action.payload.workbookNameAndHash;
      Object.assign(state, createInitialWorkbookState(), {
        workbookNameAndHash,
        workbookName: workbookNameAndHash.id,
        activeWorksheetId: state.activeWorksheetId,
        // The following properties are not coming from the backend, so we don't want to overwrite them
        printName: state.printName,
        clientChanged: state.clientChanged,
        saved: state.saved,
        changed: state.changed,
        maximizedViewId: state.maximizedViewId,
        detailViewId: state.detailViewId,
        viewMetadataById: state.viewMetadataById,
        panelById: state.panelById,
      });
      state.initializationStatus = STATUS.SUCCEEDED;
      state.lastCommand = undefined;
      setStateFromUpdate(state, action);
      patchZoomRelatedViewsState(state, action.payload);
      validateMapViewLayers(state);
      initializeOverviewMapContextsByMainMapViewId(state);
    })
    .addCase(fetchInitialState.rejected, (state) => {
      state.initializationStatus = STATUS.FAILED;
    })
    .addCase(fetchPartialState.pending, (state) => {
      state.commandStatus = STATUS.PENDING;
    })
    .addCase(fetchPartialState.fulfilled, (state, action) => {
      state.commandStatus = STATUS.SUCCEEDED;
      const { workbookNameAndHash, operations, attributeSettingsDefaults } = action.payload;
      Object.assign(state, updateWorkbookState(state, { patch: operations, attributeSettingsDefaults }), {
        workbookNameAndHash,
        workbookName: workbookNameAndHash.id,
      });
      state.commandErrorKey = '';
      state.commandErrorDetail = null;
    })
    .addCase(fetchPartialState.rejected, (state) => {
      state.commandStatus = STATUS.FAILED;
    })
    .addCase(executeCommandInternal.pending, (state, action) => {
      state.commandStatus = STATUS.PENDING;
      // Whenever a command is executed, stop live mode.
      stopLiveModeByOther(state);
      updateIsOnDemandWorksheetLoading(state, action.meta.arg);
    })
    .addCase(executeCommandInternal.fulfilled, (state, action) => {
      state.commandStatus = STATUS.SUCCEEDED;
      // cloneDeep() cannot be replaced with structuredClone() in this case,
      // because that fails to clone the Immer proxies.
      const previousActiveViewState = cloneDeep(getActiveView(state));
      const {
        workbookNameAndHash,
        operations,
        invalidViewsOrDataViews,
        invalidLayerIdsByViewId,
        dataViewsAffectedBySelectionChange,
        attributeSettingsDefaults,
      } = action.payload;
      Object.assign(
        state,
        updateWorkbookState(state, {
          patch: operations,
          attributeSettingsDefaults,
        }),
        { workbookNameAndHash, workbookName: workbookNameAndHash.id },
      );
      state.lastCommand = action.meta.arg;
      updateDataViewInvalidationState(
        state,
        invalidViewsOrDataViews,
        invalidLayerIdsByViewId,
        dataViewsAffectedBySelectionChange,
      );
      updateActiveLayerId(state, previousActiveViewState, state.lastCommand!);
      unsetMaximizedViewIfViewDoesNotExist(state, state.lastCommand!);

      const viewIds = new Set(Object.keys(state.viewById));
      state.viewMetadataById = Object.fromEntries(
        Object.entries(state.viewMetadataById).filter(([viewId]) => viewIds.has(viewId)),
      );

      patchMapViewState(state, action.payload);

      state.commandErrorKey = '';
      state.commandErrorDetail = null;
    })
    .addCase(executeCommandInternal.rejected, (state, action) => {
      const problem = action.payload as Problem | undefined;
      // if the actual error is swallowed (our default) then the `payload` is `undefined`
      state.commandErrorKey = problem?.key || '';
      state.commandErrorDetail = createCommandErrorDetails(problem);
      state.commandStatus = STATUS.FAILED;
    })
    .addCase(fetchDataDependencyState.fulfilled, (state, action) => {
      updateObjecttypeDataDependenciesState(state, action.payload);
      validateViews(state);
    });
};

/**
 * Creates an async thunk for fetching the initial workbook state.
 *
 * @return A command result (reusing the DTO from the command execution)
 */
export const fetchInitialState = createAsyncThunk(
  SLICE_PREFIX + '/fetchInitialState',
  // do not catch the error here: it would result in an infinite loading spinner
  // as the caller relies on the "rejected" state of redux as this is executed async.
  (arg: { workbookNameAndHash?: WorkbookNameAndHash } | undefined, thunkAPI) => {
    const data = document.querySelector<Data<GetWorkbookStateResult>>('#initial-workbook-state');
    if (data) {
      data.remove();
      return Promise.resolve(data.value);
    }
    return getWorkbookState(arg?.workbookNameAndHash ?? getWorkbookNameAndHash(thunkAPI.getState()));
  },
);

/**
 * Creates an async thunk for fetching a partial workbook state. Only items requested in the provided changesRequestDto will be requested from backend and updated in the workbook state.
 * Note that current implementation does not invalidate any view/dataView.
 * In case you need this for your use case, ensure that the implementation in the backed supports it.
 */
export const fetchPartialState = createAsyncThunk(
  SLICE_PREFIX + '/fetchPartialState',
  (changesRequestDto: Partial<ChangesRequestDto>, thunkAPI) => {
    return getPartialWorkbookState(getWorkbookNameAndHash(thunkAPI.getState()), {
      objecttypeIds: changesRequestDto.objecttypeIds ?? [],
      updateDataProtection: changesRequestDto.updateDataProtection ?? false,
    });
  },
);

export const fetchDataDependencyState = createAsyncThunk(
  SLICE_PREFIX + '/objecttypeDataDependencyState',
  (arg, thunkAPI) => getObjecttypesDataDependencies(getWorkbookNameAndHash(thunkAPI.getState())),
);

let currentCommandPromise: Promise<void> | undefined;

/**
 * If there is a current command promise, wait for it to complete before proceeding.
 */
export async function waitForCurrentCommand() {
  if (currentCommandPromise) {
    await currentCommandPromise;
  }
}

/**
 * Creates an async thunk for executing a command.
 *
 * Multiple commands are wrapped as Command.COMPOSITE_COMMAND.
 *
 * Only one command is executed at a time.
 *
 * @param commands - The command to be executed
 * @return The command result
 */
export function executeCommand(...commands: Command[]) {
  assert(
    commands.length === 1 || !commands.some((c) => c.type === Command.COMPOSITE_COMMAND.type),
    'cannot merge composite commands',
  );
  return commands.length > 1 ?
      executeCommandInternal(Command.COMPOSITE_COMMAND({ commands }))
    : executeCommandInternal(commands[0]);
}

const executeCommandInternal = createAsyncThunk(SLICE_PREFIX + '/executeCommand', (command: Command, thunkAPI) => {
  logger.log('Schedule command', command);

  /*
   * Promise.prototype.finally() won't work, because the return value of the final callback
   * is ignored, i.e. Promise.resolve(1).finally(() => 2) will be resolved with 1.
   * See https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/Promise/finally
   */
  const promise = Promise.allSettled([currentCommandPromise])
    .then(() => {
      logger.info('Execute command', command);
      return executeCommandRequest(getWorkbookNameAndHash(thunkAPI.getState()), command);
    })
    .catch((error) => {
      logger.error('Failed to execute command', command, error);
      // In order to pass the error to the `rejected` callback, we need to use this Redux method
      // instead of just throwing the error here.
      return thunkAPI.rejectWithValue(error);
    })
    .finally(() => {
      currentCommandPromise = undefined;
    }) as Promise<ExecuteCommandResult>;

  // currentCommandPromise is used to block the next commands from executing until the first
  // command is fulfilled (including store update). Since redux will write the data into store
  // after we return from this method (payloadCreator), we need to ensure that we fulfill the
  // currentCommandPromise afterward. That's why we need an additional timeout() here.
  currentCommandPromise = promise.then((result) => {
    logger.log('Command executed', command, result);
    return timeout();
  });

  return promise;
});

function updateObjecttypeDataDependenciesState(
  state: WorkbookState,
  objecttypeDataDependencies: ObjecttypesDataDependenciesDto,
) {
  state.objecttypeDataDependencies = objecttypeDataDependencies;
}

function hasDirtyViews(nextState: WorkbookState) {
  return nextState.invalidViewsOrDataViews.length > 0;
}

/**
 * Activates the only view of the active worksheet if the designer is visible.
 */
function activateOnlyViewIfAvailable(state: WorkbookState) {
  const views = getActiveWorksheetViews(state);
  if (isPanelVisible(state, 'd-view-settings-panel') && views.length === 1) {
    state.activeViewId = views[0].id;
  }
}

function removeObjecttypesById(
  {
    objecttypeById,
    attributeById,
    dataSources,
  }: Pick<WorkbookState, 'objecttypeById' | 'attributeById' | 'dataSources'>,
  objecttypeIdsToRemove: ObjecttypeIdString[],
) {
  assert(Array.isArray(objecttypeIdsToRemove), 'objecttypeIdsToRemove has to be an array');
  // remove object types and their attributes
  for (const objecttypeId of objecttypeIdsToRemove) {
    if (!objecttypeById[objecttypeId]) {
      continue;
    }
    const attributeIds = objecttypeById[objecttypeId].attributes;
    for (const attributeId of attributeIds) {
      delete attributeById[attributeId];
    }
    // If we removed enrichment object type we have to do cleanup.
    // Code below removes orphan references to deleted enrichment stored in enriched objecttypes enrichment array.
    const enrichments = objecttypeById[objecttypeId].enrichments;
    if (enrichments) {
      const enrichmentIds = enrichments.map((enrichment) => enrichment.objecttypeId);
      removeObjecttypesById({ objecttypeById, attributeById, dataSources }, enrichmentIds);
    } else {
      // this removes all the references
      for (const objecttype of Object.values(objecttypeById)) {
        if (objecttype.enrichments) {
          objecttype.enrichments = objecttype.enrichments.filter(
            (enrichment) => !objecttypeIdsToRemove.includes(enrichment.objecttypeId),
          );
        }
      }
    }
    delete objecttypeById[objecttypeId];
  }

  // remove obsolete dataSources
  const uniqueDataSourceIds = new Set(Object.values(objecttypeById).map(({ dataSourceName }) => dataSourceName));
  const newDataSources = dataSources.filter(({ itemId }) => uniqueDataSourceIds.has(itemId.itemName));

  return { objecttypeById, attributeById, dataSources: newDataSources };
}

function activateViewInternally(state: WorkbookState, viewId?: ViewId) {
  if (viewId && isWorksheetOnDemandOverlayVisible(state)) {
    return; // don't activate a view when the worksheet on demand overlay is visible
  }
  const layerFromNewlyActiveView =
    viewId != null && state.activeLayerId != null && layerById(state, viewId, state.activeLayerId);
  if (layerFromNewlyActiveView == null) {
    delete state.activeLayerId;
  }
  state.activeViewId = viewId;
}

function updateActiveLayerId(
  state: WorkbookState,
  previousActiveViewState: ViewWithMetadata | undefined,
  command: Command,
) {
  const activeView = getActiveView(state);
  if (activeView && previousActiveViewState) {
    const viewChangedToMap = activeView.type === ViewType.MAP && previousActiveViewState.type !== ViewType.MAP;
    if (isCommandType(command, Command.ADD_LAYER_GROUP)) {
      const layerIdsBefore = new Set(getViewLayerIds(previousActiveViewState));
      const layerIdsAfter = getViewLayerIds(activeView);
      state.activeLayerId = layerIdsAfter.find((newId) => !layerIdsBefore.has(newId));
    } else if (viewChangedToMap && isCommandType(command, Command.CHANGE_VISUALIZATION)) {
      state.activeLayerId = command.payload.dataViewId;
    } else if (viewChangedToMap && isCommandType(command, Command.ADD_ATTRIBUTE)) {
      state.activeLayerId = activeView.dataViews.at(-1);
    }
  }
  const activeLayerId = state.activeLayerId;
  if (
    activeLayerId &&
    !Object.values(state.viewById).some(
      (view) => view.layerDefinitions?.[activeLayerId] != null || view.dataViews.includes(activeLayerId as DataViewId),
    )
  ) {
    delete state.activeLayerId;
  }
}

function unsetMaximizedViewIfViewDoesNotExist(state: WorkbookState, command: Command) {
  if (isCommandType(command, Command.REMOVE_VIEW)) {
    const maximizedViewId = getMaximizedViewId(state);
    if (maximizedViewId && getViewById(state, maximizedViewId) == null) {
      state.maximizedViewId = '';
    }
  }
}

function createCommandErrorDetails(serverErrorPayload?: Problem) {
  if (
    serverErrorPayload?.key === WORKBOOK_ERROR_KEYS.NO_SPACE_FOR_VIEW_IN_WORKSHEET_GRID &&
    'worksheetId' in serverErrorPayload
  ) {
    return { worksheetId: serverErrorPayload.worksheetId };
  }
}

function updateDataViewInvalidationState(
  state: WorkbookState,
  invalidViewsOrDataViews: (ViewId | DataViewId)[],
  invalidLayerIdsByViewId: Record<ViewId, LayerId[]>,
  dataViewsAffectedBySelectionChange: DataViewId[],
) {
  state.invalidViewsOrDataViews = invalidViewsOrDataViews;
  state.invalidLayerIdsByViewId = invalidLayerIdsByViewId;
  state.dataViewsAffectedBySelectionChange = dataViewsAffectedBySelectionChange;

  if (
    !getActiveView(state) || // If view was deleted
    (isActiveWorksheetDirty(state) && isActiveWorksheetOnDemand(state))
  ) {
    activateViewInternally(state); // Deactivate active view
  }
}

function getViewLayerIds(view: ViewWithMetadata) {
  return Object.keys(view.layerDefinitions ?? {}) as LayerId[];
}

function setStateFromUpdate(
  state: WorkbookState,
  action: PayloadAction<
    GetWorkbookStateResult,
    string,
    {
      arg: unknown;
    }
  >,
) {
  const {
    operations,
    invalidViewsOrDataViews,
    invalidLayerIdsByViewId,
    dataViewsAffectedBySelectionChange,
    version,
    publicationStage,
    attributeSettingsDefaults,
  } = action.payload;
  Object.assign(state, updateWorkbookState(state, { patch: operations, attributeSettingsDefaults }), {
    version,
    publicationStage,
  });
  updatePanelStateFromDisplayState(state);
  state.clientChanged = false;

  state.unavailableObjecttypeIds = action.payload.unavailableObjecttypeIds ?? [];
  updateDataViewInvalidationState(
    state,
    invalidViewsOrDataViews,
    invalidLayerIdsByViewId,
    dataViewsAffectedBySelectionChange,
  );
}

function doAddNotifications(state: RootStateOr<WorkbookState>, viewId: ViewId, added: WorkbookNotification[]) {
  const viewNotifications = getNotificationsForViewByNotificationId(state, viewId);
  added.forEach((notification) => (viewNotifications[notification.id] = notification));
}

function removeNotificationBySource(state: WorkbookState, viewId: ViewId, source: NotificationSource) {
  const notificationsById = state.notificationsByView[viewId] || {};
  Object.values(notificationsById).forEach((notification) => {
    if (notification.source === source) {
      delete notificationsById[notification.id];
    }
  });
}

/* No changes must be made here unless there is a change made in WorkbookAttributeDto.java#setDefaultAndCommonValues
 * The goal is to reduce unnecessary overhead transporting default or common values even if they differ from the
 * type intrinsic defaults. It is okay if defaults here are (at some point in time) not the actual logical defaults
 * on UI or business logic level, we would just transport unnecessary information, but the correctness would not suffer
 * as long as the values stay in sync with the backend */
function getAttributeDefaults() {
  return {
    status: 'OK',
    category: 'DIMENSION',
    keyAttribute: false,
    type: 'STRING',
    requestAllDimension: false,
    totalsAggregation: 'COUNT_DISTINCT',
    defaultTotalsAggregation: 'COUNT_DISTINCT',
    fractionCalculation: 'NONE',
    fractionFormat: '0%',
    hasGeometryRelation: false,
    hasLobRelation: false,
    colorPaletteId: 'colorpalette-workbookDefault',
    defaultColorPaletteId: 'colorpalette-workbookDefault',
    usableInCondition: true,
    visible: true,
    visibleInDataBrowser: true,
    customAggregationFunctionAttribute: false,
    fromCombinedObjecttype: false,
    attributeAcquisition: {
      required: false,
      restrictToAllowedValues: false,
      editingPermission: 'ALLOW_EDITING',
    },
    attributeColorSettings: {
      colorRules: [],
    },
    sortDirection: 'ASCENDING',
    ignoreSortAttribute: false,
  } satisfies Partial<WorkbookAttributeDto>;
}

/**
 * Updates the given workbook state with a patch (command response) from the server.
 *
 * @param state - The state object to update
 * @param context
 * @param context.patch - A JSON patch
 * @param context.attributeSettingsDefaults - In case the patch includes attributes
 * @return The updated state object
 */
function updateWorkbookState(
  state: WorkbookState,
  {
    patch,
    attributeSettingsDefaults,
  }: { patch: Operation[]; attributeSettingsDefaults?: Record<`${AttributeModificationKey}.default`, unknown> },
) {
  // Attribute setting default are sent to the client only once,
  // but the settings utils expect them in every attribute.
  // So we add them here.
  for (const op of patch) {
    if (op.op === 'replace' && (op.path.startsWith('/attributeById') || op.path.startsWith('/activeAttributeById'))) {
      const attribute = op.value as WorkbookAttributeDto;
      const attributeDefaults = getAttributeDefaults();
      // Attribute needs to be copied, because the object is frozen.
      op.value = {
        ...attributeDefaults,
        ...attribute,
        settings: { ...attribute.settings, ...attributeSettingsDefaults },
      };
    }
  }
  const nextState = applyPatch(state, patch).newDocument;
  const activeWorksheet = getActiveWorksheet(state);
  if (
    !activeWorksheet ||
    (isWorksheetOnDemand(activeWorksheet) &&
      shouldOnDemandBeConsidered(state, slice(state).lastCommand) &&
      hasDirtyViews(nextState))
  ) {
    state.activeViewId = undefined;
  }
  validateViews(nextState);
  return nextState;
}

const stopLiveModeByOther = (state: Draft<WorkbookState>) => {
  state.liveModeState = { state: 'stopped', silent: false };
};

function patchZoomRelatedViewsState(state: WorkbookState, result: ExecuteCommandResult) {
  // If the ExecuteCommandResult does not include new auto-zoom views,
  // we keep existing ones: If there are some, then the auto-zoom was not done, yet.
  state.zoomRelatedViewIds =
    result.viewsToZoomToDataExtent?.length ?
      [...new Set(result.viewsToZoomToDataExtent).intersection(new Set(keys(state.viewById)))]
    : state.zoomRelatedViewIds;
}

function initializeOverviewMapContextsByMainMapViewId(state: RootStateOr<WorkbookState>) {
  // only for initializing (we need the initial extents from main map views for report printing)
  getViews(state)
    .filter((view) => isOverviewMap(state, view.id))
    .forEach((view) => {
      const viewId: ViewId = view.id;
      const overviewMapViewContext = getMapViewContext(state, viewId);
      const mainMapViewId = overviewMapViewContext.overviewMainMapViewId;
      assertNonNullable(mainMapViewId);
      const mainMapViewContext = getViewById(state, mainMapViewId).mapViewContext;
      const currentExtent = mainMapViewContext?.currentExtent as Extent;
      slice(state).overviewMapContextsByMainMapViewId[mainMapViewId] = Object.assign(
        getOverviewMapContextByMainMapViewId(slice(state), mainMapViewId) ?? {},
        { currentExtent, initial: true },
      );
    });
}

function patchMapViewState(state: WorkbookState, result: ExecuteCommandResult) {
  patchZoomRelatedViewsState(state, result);
  validateMapViewLayers(state);
  updateOverviewMapContextsByMainMapViewId(state);
}

function updateOverviewMapContextsByMainMapViewId(state: WorkbookState) {
  // remove all views that are no longer main map views
  Object.keys(state.overviewMapContextsByMainMapViewId).forEach((key) => {
    const viewId = key as ViewId;
    if (!isMainMapView(state, viewId as ViewId)) {
      delete state.overviewMapContextsByMainMapViewId[viewId];
    }
  });
  // the new entries are added only from main map interactions with the state (we need calculated extent, not the one from the backend,
  // because it would be not always correct) => see setMainMapContextForOverviewMap calls
}
